import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/index';
import { api } from '../lib/api';
import { WorkspaceGroup, User } from '../types';
import { Trash2 } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialogProvider';

// Tree Node Component
// A user can be in several groups (users.group_ids, migration 045); older
// payloads only carry the single primary group_id.
const groupIdsOf = (m: { group_ids?: string[]; group_id?: string | null }): string[] =>
  m.group_ids ?? (m.group_id ? [m.group_id] : []);

const TreeNode: React.FC<{
  node: any;
  level: number;
  onToggle: (nodeId: string) => void;
  expandedNodes: Set<string>;
  isSuperAdmin?: boolean;
  currentUserId?: string;
  roleOptions?: { value: string; label: string }[];
  groupOptions?: { id: string; name: string }[];
  onChangeRole?: (userId: string, role: string) => void;
  onMoveGroup?: (userId: string, groupId: string | null) => void;
  onRemoveUser?: (userId: string, name: string) => void;
}> = ({ node, level, onToggle, expandedNodes, isSuperAdmin, currentUserId, roleOptions, groupOptions, onChangeRole, onMoveGroup, onRemoveUser }) => {
  const { t } = useTranslation();
  const canManageThisUser = isSuperAdmin && node.type === 'user' && node.id !== currentUserId;
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const indent = level * 20;

  const getNodeIcon = () => {
    switch (node.type) {
      case 'group':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'user':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'task':
        return (
          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'incident':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusBadge = () => {
    if (node.type === 'task') {
      let statusColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      let statusText = node.status;

      switch (node.status) {
        case 'todo':
          statusColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          statusText = t('team.tree.taskStatus.todo');
          break;
        case 'inProgress':
          statusColor = 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30';
          statusText = t('team.tree.taskStatus.inProgress');
          break;
        case 'review':
          statusColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
          statusText = t('team.tree.taskStatus.review');
          break;
        case 'done':
          statusColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
          statusText = t('team.tree.taskStatus.done');
          break;
      }

      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
          {statusText}
        </span>
      );
    }

    if (node.type === 'incident') {
      let severityColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      let severityText = node.severity;

      switch (node.severity) {
        case 'low':
          severityColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
          severityText = t('team.tree.severity.low');
          break;
        case 'medium':
          severityColor = 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30';
          severityText = t('team.tree.severity.medium');
          break;
        case 'high':
          severityColor = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
          severityText = t('team.tree.severity.high');
          break;
        case 'critical':
          severityColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
          severityText = t('team.tree.severity.critical');
          break;
      }

      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${severityColor}`}>
          {severityText}
        </span>
      );
    }

    return null;
  };

  return (
    <div>
      <div
        className="flex items-center py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
        style={{ paddingLeft: `${indent}px` }}
        onClick={() => hasChildren && onToggle(node.id)}
      >
        {hasChildren && (
          <button className="mr-2 p-1">
            {isExpanded ? (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        )}

        {!hasChildren && <div className="w-6 mr-2" />}

        <div className="flex items-center gap-2 flex-1">
          {getNodeIcon()}
          <span className="font-medium text-gray-900 dark:text-gray-100">{node.name || node.title}</span>
          {node.role && <span className="text-sm text-gray-500 dark:text-gray-400">({node.role})</span>}
          {node.email && <span className="text-sm text-gray-400 dark:text-gray-500">- {node.email}</span>}
          {getStatusBadge()}
          {canManageThisUser && (
            <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <select
                value={node.role}
                onChange={(e) => onChangeRole && onChangeRole(node.id, e.target.value)}
                title={t('team.tree.actions.roleLabel')}
                className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {(roleOptions || []).map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <select
                value={node.group_id || ''}
                onChange={(e) => onMoveGroup && onMoveGroup(node.id, e.target.value || null)}
                title={t('team.tree.actions.groupLabel')}
                className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('team.tree.actions.ungroupedOption')}</option>
                {(groupOptions || []).map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onRemoveUser && onRemoveUser(node.id, node.name)}
                title={t('team.tree.actions.remove')}
                className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child: any) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              isSuperAdmin={isSuperAdmin}
              currentUserId={currentUserId}
              roleOptions={roleOptions}
              groupOptions={groupOptions}
              onChangeRole={onChangeRole}
              onMoveGroup={onMoveGroup}
              onRemoveUser={onRemoveUser}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// A single membership cell — independent checkbox: a user can be in several
// groups (migration 045), so ticking adds the group and unticking removes it.
// The "ungrouped" column is the exception: it can only be ticked (it clears
// every group) and is disabled while it already is.
const MatrixCell: React.FC<{ isActive: boolean; disabled: boolean; onClick: () => void; label: string }> = ({ isActive, disabled, onClick, label }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={isActive}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={`w-5 h-5 rounded border-2 flex items-center justify-center mx-auto transition-colors ${
      isActive
        ? 'bg-blue-600 border-blue-600'
        : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {isActive && <span className="w-2 h-2 rounded-sm bg-white" />}
  </button>
);

// User x Group permission matrix — shown to superadmin AND manager (the
// redesign of the group/role management UI). Deliberately does NOT
// introduce any new authorization concept: it's a visual, more
// discoverable front end for the exact same two fields the tree view
// already edited (users.role, users.group_id). A manager sees every row
// but cannot act on a superadmin target (backend-enforced too — this
// just disables the controls so it doesn't just 403 silently).
const PermissionMatrix: React.FC<{
  members: User[];
  groups: WorkspaceGroup[];
  roleOptions: { value: string; label: string }[];
  currentUserId?: string;
  currentUserIsSuperAdmin: boolean;
  onChangeRole: (userId: string, role: string) => void;
  onSetGroups: (userId: string, groupIds: string[]) => void;
  onRemoveUser: (userId: string, name: string) => void;
}> = ({ members, groups, roleOptions, currentUserId, currentUserIsSuperAdmin, onChangeRole, onSetGroups, onRemoveUser }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filteredMembers = members.filter((m) => {
    const q = search.toLowerCase();
    return !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('team.matrix.searchPlaceholder')}
        className="w-full max-w-xs px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('team.matrix.user')}</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('team.matrix.role')}</th>
              {groups.map((g) => (
                <th key={g.id} className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{g.name}</th>
              ))}
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('team.matrix.ungrouped')}</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredMembers.length === 0 && (
              <tr>
                <td colSpan={groups.length + 4} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {t('team.matrix.noResults')}
                </td>
              </tr>
            )}
            {filteredMembers.map((member) => {
              const isSelf = member.id === currentUserId;
              // A manager (not superadmin) can't act on a superadmin
              // target — matches the backend's UsersService guard.
              const isProtected = member.role === 'superadmin' && !currentUserIsSuperAdmin;
              const rowDisabled = isSelf || isProtected;
              const memberGroupIds = groupIdsOf(member);
              return (
                <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{member.email}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <select
                      value={member.role}
                      onChange={(e) => onChangeRole(member.id, e.target.value)}
                      disabled={rowDisabled}
                      title={isProtected ? t('team.matrix.superadminProtected') : t('team.tree.actions.roleLabel')}
                      className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      {roleOptions.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </td>
                  {groups.map((g) => (
                    <td key={g.id} className="px-2 py-2">
                      <MatrixCell
                        isActive={memberGroupIds.includes(g.id)}
                        disabled={rowDisabled}
                        onClick={() => onSetGroups(
                          member.id,
                          memberGroupIds.includes(g.id) ? memberGroupIds.filter((id) => id !== g.id) : [...memberGroupIds, g.id]
                        )}
                        label={t('team.matrix.moveToGroup', { group: g.name })}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <MatrixCell
                      isActive={memberGroupIds.length === 0}
                      disabled={rowDisabled || memberGroupIds.length === 0}
                      onClick={() => onSetGroups(member.id, [])}
                      label={t('team.matrix.moveToUngrouped')}
                    />
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!isSelf && !isProtected && (
                      <button
                        type="button"
                        onClick={() => onRemoveUser(member.id, member.name)}
                        title={t('team.tree.actions.remove')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 text-xs font-medium"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('team.tree.actions.remove')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// `embedded`: true when rendered inside Settings' combined "Users & Groups"
// section, which already has its own page header — skips the redundant
// inner one. Standalone /team keeps redirecting here isn't a thing anymore
// (App.tsx now redirects /team -> /settings), but the component stays
// usable stand-alone for that transition period.
const Team: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  // Managers get the same matrix as superadmin, minus anything touching a
  // superadmin target (enforced both here for UX and in the backend).
  const canManage = isSuperAdmin || currentUser?.role === 'manager';
  const [actionError, setActionError] = useState<string | null>(null);

  const allRoleOptions = [
    { value: 'superadmin', label: 'Superadmin' },
    { value: 'manager', label: 'Manager' },
    { value: 'operator', label: 'Operator' },
    { value: 'client_user', label: 'Client User' }
  ];
  // A manager can never grant superadmin (backend rejects it too) — drop
  // it from the dropdown entirely so it isn't offered as a dead-end choice.
  const roleOptions = isSuperAdmin ? allRoleOptions : allRoleOptions.filter((r) => r.value !== 'superadmin');

  const refetchTeamData = async () => {
    const results = await Promise.allSettled([
      api.get('/workspace/groups'),
      api.get('/workspace/members')
    ]);
    const [groupResult, memberResult] = results;
    if (groupResult.status === 'fulfilled') {
      const groupData = (groupResult.value.data as any)?.data;
      if (Array.isArray(groupData)) setGroups(groupData);
    }
    if (memberResult.status === 'fulfilled') {
      const memberData = (memberResult.value.data as any)?.data;
      if (Array.isArray(memberData)) setMembers(memberData);
    }
  };

  const handleChangeRole = async (userId: string, role: string) => {
    setActionError(null);
    try {
      await api.put(`/users/${userId}`, { role });
      await refetchTeamData();
    } catch {
      setActionError(t('team.tree.actions.roleUpdateError'));
    }
  };

  const handleMoveGroup = async (userId: string, groupId: string | null) => {
    setActionError(null);
    try {
      await api.put(`/users/${userId}`, { group_id: groupId });
      await refetchTeamData();
    } catch {
      setActionError(t('team.tree.actions.groupUpdateError'));
    }
  };

  const handleSetGroups = async (userId: string, groupIds: string[]) => {
    setActionError(null);
    try {
      await api.put(`/users/${userId}`, { group_ids: groupIds });
      await refetchTeamData();
    } catch {
      setActionError(t('team.tree.actions.groupUpdateError'));
    }
  };

  const handleRemoveUser = async (userId: string, name: string) => {
    if (!(await confirm(t('team.tree.actions.removeConfirm', { name }), { variant: 'danger' }))) return;
    setActionError(null);
    try {
      await api.delete(`/users/${userId}`);
      await refetchTeamData();
    } catch {
      setActionError(t('team.tree.actions.removeError'));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        api.get('/workspace/groups'),
        api.get('/workspace/members')
      ]);

      const [groupResult, memberResult] = results;

      if (groupResult.status === 'fulfilled') {
        const groupData = (groupResult.value.data as any)?.data;
        if (Array.isArray(groupData)) {
          setGroups(groupData);
        }
      }

      if (memberResult.status === 'fulfilled') {
        const memberData = (memberResult.value.data as any)?.data;
        if (Array.isArray(memberData)) {
          setMembers(memberData);
        }
      }

      if (groupResult.status === 'rejected' && memberResult.status === 'rejected') {
        setError(t('team.structure.errors.both'));
      } else if (groupResult.status === 'rejected') {
        setError(t('team.structure.errors.groups'));
      } else if (memberResult.status === 'rejected') {
        setError(t('team.structure.errors.members'));
      }

      setLoading(false);
    };

    fetchData();
  }, [t]);

  const buildTreeData = () => {
    const tree: any[] = groups.map(g => ({
      id: g.id,
      name: g.name,
      type: 'group',
      color: g.color,
      children: members
        .filter(m => groupIdsOf(m).includes(g.id))
        .map(m => ({
          id: m.id,
          name: m.name,
          role: m.role,
          email: m.email,
          group_id: m.group_id,
          type: 'user' as const,
          children: [] as any[]
        }))
    }));

    const ungrouped = members.filter(m => groupIdsOf(m).length === 0);
    if (ungrouped.length > 0) {
      tree.push({
        id: 'ungrouped',
        name: t('team.structure.ungrouped'),
        type: 'group',
        children: ungrouped.map(m => ({
          id: m.id,
          name: m.name,
          role: m.role,
          email: m.email,
          group_id: m.group_id,
          type: 'user' as const,
          children: [] as any[]
        }))
      });
    }

    return tree;
  };

  useEffect(() => {
    if (!loading && (groups.length > 0 || members.length > 0)) {
      const groupIds = groups.map(g => g.id);
      if (members.some(m => groupIdsOf(m).length === 0)) {
        groupIds.push('ungrouped');
      }
      setExpandedNodes(new Set(groupIds));
    }
  }, [loading, groups, members]);

  const handleToggle = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const treeData = buildTreeData();

  return (
    <div className="space-y-4">
      {!embedded && (
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('team.teamManagement')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('team.subtitle')}</p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {canManage ? t('team.matrix.title') : t('team.structure.title')}
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {canManage ? t('team.matrix.description') : t('team.structure.description')}
          </p>
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="ml-3 text-gray-600 dark:text-gray-400">{t('team.structure.loading')}</span>
            </div>
          )}

          {!loading && error && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg mb-4">
              {actionError}
            </div>
          )}

          {!loading && canManage && members.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('team.structure.noData')}
            </div>
          )}

          {!loading && canManage && members.length > 0 && (
            <PermissionMatrix
              members={members}
              groups={groups}
              roleOptions={roleOptions}
              currentUserId={currentUser?.id}
              currentUserIsSuperAdmin={isSuperAdmin}
              onChangeRole={handleChangeRole}
              onSetGroups={handleSetGroups}
              onRemoveUser={handleRemoveUser}
            />
          )}

          {!loading && !canManage && treeData.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('team.structure.noData')}
            </div>
          )}

          {!loading && !canManage && treeData.length > 0 && (
            <div className="space-y-1">
              {treeData.map((group) => (
                <TreeNode
                  key={group.id}
                  node={group}
                  level={0}
                  onToggle={handleToggle}
                  expandedNodes={expandedNodes}
                  isSuperAdmin={isSuperAdmin}
                  currentUserId={currentUser?.id}
                  roleOptions={roleOptions}
                  groupOptions={groups}
                  onChangeRole={handleChangeRole}
                  onMoveGroup={handleMoveGroup}
                  onRemoveUser={handleRemoveUser}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Team;